import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import useSWR from 'swr';
import { BareFetcher, PublicConfiguration } from 'swr/_internal';
import useSWRMutation, { SWRMutationConfiguration } from 'swr/mutation';

import { ApiError, ApiResponseAsync, isApiError } from 'container/models';
import { useApplicationErrorHandling } from 'hooks/useApplicationErrorHandling';
import { ApplicationError } from 'types/errorHandling';

import { ApiActions } from './actions';
import { authCache } from './cache';
import { UseAPIResult, UseReadAPIResult } from './useAPI';

type ServicesCacheKey = undefined | null | string | any[];

export const refreshIntervalInConfigModeInSeconds = 10;

const ErrDetailsKey = Symbol('useAPIWithServices arguments');

function isString(value: any): value is string {
  return typeof value === 'string';
}

function buildCacheKeyFromArray(value: any[]): string[] | undefined {
  const undefinedElementIndex = value.findIndex((item) => !item);
  if (undefinedElementIndex >= 0) return undefined;
  return value.map((item) => {
    if (isString(item)) return item;
    if (Object.prototype.hasOwnProperty.call(item, 'id')) {
      return item['id'] as string;
    }
    return JSON.stringify(item);
  });
}

function buildCacheKey(key: ServicesCacheKey): undefined | string | string[] {
  if (!key) return undefined;
  if (isString(key)) return key;
  return buildCacheKeyFromArray(key);
}

type WriteAPIWithServicesResponse<Payload, Response> =
  UseAPIResult<Response> & {
    isExecuting: boolean;
    trigger: [Payload] extends [never]
      ? () => Promise<Response>
      : (payload: Payload) => Promise<Response>;
    reset: () => void;
  };

const timeSecond = 1000;
const timeMinute = 60 * timeSecond;
const timeHour = 60 * timeMinute;

export enum ReadingCacheType {
  Static = 'static',
  Fresh = 'fresh',
  /** focal point of the configuration page (like get fields for fields configuration page) */
  ConfigurationMainEntity = 'configuration_main_entity',
  /** dependencies of the focal point (like get process draft for the field configuration page) */
  ConfigurationDependency = 'configuration_dependency',
  /** focal point of the execution page (like get instances for a list of instances page) */
  ExecutionMainEntity = 'execution_main_entity',
  /** dependencies of the focal point that are related to the process release (like an indexing scenario) */
  ExecutionReleaseDependency = 'execution_release_dependency',
  /** dependencies of the focal point that are related to the process draft (like process aliases) */
  ExecutionDraftDependency = 'execution_draft_dependency',
}

function buildCacheConfigurationFromCacheType(
  cacheType: ReadingCacheType
): Pick<
  Partial<PublicConfiguration>,
  'dedupingInterval' | 'revalidateOnMount'
> {
  switch (cacheType) {
    case ReadingCacheType.Static:
      return {
        dedupingInterval: 24 * timeHour,
      };
    case ReadingCacheType.Fresh:
      return {
        dedupingInterval: 10 * timeSecond,
      };
    case ReadingCacheType.ConfigurationMainEntity:
      return {
        revalidateOnMount: true,
      };
    case ReadingCacheType.ConfigurationDependency:
      return {
        dedupingInterval: 5 * timeMinute,
      };
    case ReadingCacheType.ExecutionMainEntity:
      return {
        revalidateOnMount: true,
      };
    case ReadingCacheType.ExecutionReleaseDependency:
      return {
        dedupingInterval: timeHour,
      };
    case ReadingCacheType.ExecutionDraftDependency:
      return {
        dedupingInterval: 9 * timeMinute,
      };
    default:
      throw new Error(`unsupported cache type ${cacheType}`);
  }
}

/*
  We can come back later, think more, experiment and test thoroughly.
  This middleware affects the whole application.
*/
/*
const wrappedDataMiddleware: Middleware =
  (useSWRNext) => (key, fetcher, config) => {
    const result = useSWRNext(key, fetcher, config);

    const [suppressData, setSuppressData] = useState(
      !!config.revalidateOnMount
    );

    useEffect(() => {
      if (suppressData && !result.isValidating) setSuppressData(false);
    }, [suppressData, result.isValidating]);

    if (suppressData) {
      return {
        ...result,
        data: undefined,
        isLoading: true,
        isValidating: true,
      };
    }

    return result;
  };
 */

/**
 * useReadAPIWithServices wrapper around action using SWR;
 * key is usually array that starts with globally unique action name and
 * includes all action dependencies; if dependency is some object with id then it's replaced internally with
 * a value of the property; if at least one member of array is undefined then key passed to SWR will be also be undefined
 *
 * A method to get information from the server and store it within cache.
 * @param key an identifier for the piece of information you're storing in cache, if this is not defined on call, then
 * a call to the server will be made and then stored in the cache under this name. If it is found, then it returns the
 * value associated with this key. Every property you pass to action should appear in the key.
 * @param action a function that returns a response, i.e., search, get, etc
 * @param opts optional parameters
 */
export const useReadAPIWithServices = <Response>(
  /** Either string or array of action dependencies. */
  key: ServicesCacheKey,

  /** Action being wrapped by SWR. */
  action: () => ApiResponseAsync<Response>,

  opts: {
    cacheType: ReadingCacheType;
    noErrorRetryFor400?: boolean;
  }
): UseReadAPIResult<Response> => {
  const handleApplicationError = useApplicationErrorHandling();
  const history = useHistory();
  const dispatch = useDispatch();

  const swrKey = buildCacheKey(key);

  // define a fetcher; handle application errors by wrapping them in a rejected promise
  const fetcher = async () => {
    const response = await action();
    return await (isApiError(response)
      ? // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
        Promise.reject(response)
      : Promise.resolve(response));
  };

  const cacheConfig = buildCacheConfigurationFromCacheType(opts.cacheType);
  // define config that provides error handling by default
  const config = useMemo(
    (): Partial<
      PublicConfiguration<Response, ApiError, BareFetcher<Response>>
    > => ({
      onError(err, key) {
        const appErr = ApplicationError.FromError(
          err,
          `Failed to fetch ${key}`
        );

        appErr.details ??= {};

        appErr.details[ErrDetailsKey] = {
          hook: 'useReadAPIWithServices',
          key,
          action,
          opts,
        };

        handleApplicationError(appErr);

        // Move to log in screen if 401 Unauthorized response from server - clears caches
        if (err.status === 401) {
          dispatch(ApiActions.signedOut()); // redux cache
          authCache.clear(); // browser cache
          history.push('/');
        }
      },
      shouldRetryOnError: (err) =>
        err.status !== 400 || !opts.noErrorRetryFor400,

      revalidateIfStale: true,
      revalidateOnFocus: false,
      revalidateOnReconnect: true,

      ...cacheConfig,

      // use: [wrappedDataMiddleware],
    }),
    [action, cacheConfig, dispatch, handleApplicationError, history, opts]
  );

  const result = useSWR<Response, ApiError>(swrKey, fetcher, config);
  const { mutate, data, error, isLoading } = result;

  const wrappedMutate = useCallback(() => mutate(undefined), [mutate]);

  return {
    data,
    error,
    isLoading,
    mutate: wrappedMutate,
  };
};

/**
 * Sends information to the server. The Payload is the business data going in, and Response is the business
 * data going out.
 * @param key uniquely identify the request. Typically comprised of all parameters used in the action
 * @param action what you want to do with the server
 * @param opts optional parameters
 */
export const useWriteAPIWithServices = <Payload, Response>(
  /** Either string or array of action dependencies. */
  key: ServicesCacheKey,

  /** Action being wrapped by SWR. */
  action: (payload: Payload) => ApiResponseAsync<Response>,

  /** Additional options. */
  opts?: {
    /** Function to call when a request succeeds. One use for this is for calling a {@link useReadAPI} `mutate` function to tell it to update. */
    onSuccess?: (data: Response) => void;
  }
): WriteAPIWithServicesResponse<Payload, Response> => {
  const handleApplicationError = useApplicationErrorHandling();

  const swrKey = buildCacheKey(key);

  const fetcher = useCallback(
    (_: string, { arg }: { arg: Payload }) =>
      action(arg).then((response) =>
        isApiError(response)
          ? Promise.reject(response)
          : Promise.resolve(response)
      ),
    [action]
  );

  const config = useMemo(
    (): Partial<
      SWRMutationConfiguration<Response, ApiError, ServicesCacheKey, Payload>
    > => ({
      onError(error: ApiError, key: string) {
        const appErr = ApplicationError.FromError(
          error,
          `Failed to write ${key}`
        );

        appErr.details ??= {};

        appErr.details[ErrDetailsKey] = {
          hook: 'useWriteApiWithServices',
          key,
          action,
          opts,
        };

        handleApplicationError(appErr);
      },
      onSuccess(data) {
        opts?.onSuccess?.(data);
      },
    }),
    [action, handleApplicationError, opts]
  );

  const { isMutating, data, error, trigger, reset } = useSWRMutation<
    Response,
    ApiError,
    ServicesCacheKey,
    Payload
  >(swrKey, fetcher, config);

  return {
    isExecuting: isMutating,
    data,
    error,
    trigger,
    reset,
  };
};
